import { extensionOf } from "@/analytics/hygiene";
import { ROUTES } from "@/constants/routes";
import { useClosePane } from "@/hooks/pane-routing";
import { useIsPublicCanvasView } from "@/public-canvas/PublicCanvasArtifactContext";
import { dropPresignedFields, isRestoredQueryData } from "@/queryPersistence";
import {
  trackFileArchived,
  trackFileCopied,
  trackFileMoved,
  trackFilePublished,
  trackFileRenamed,
  trackFileRestored,
  trackFileUnpublished,
  trackFileVersionRestored,
  trackFolderCreated,
} from "@/utils/analytics";
import { route } from "@/utils/route";
import type {
  AddFileRecordToWorkspaceResponse,
  CopyFileRecordPayload,
  CreateFileRecordPayload,
  CreateFileRecordReferencePayload,
  CreateFileRecordReferenceResponse,
  FileRecord,
  FileRecordComponents,
  FileRecordKind,
  FileRecordVersion,
  MoveFileRecordPayload,
  PatchFileRecordRegionPayload,
  PatchFileRecordRegionResponse,
  PresignUploadResponse,
  PublishManyResult,
  UnpublishManyResult,
} from "@cohesive/api-client";
import type { Entity } from "@cohesive/api-entities";
import { getPresignedUrlExpiresAtMs } from "@cohesive/ui-components";
import {
  hashKey,
  useMutation,
  UseMutationOptions,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  type QueryKey,
} from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { orgAPI } from "./api";
import {
  findCachedFileRecord,
  findRowInList,
  invalidateCachedFileRecord,
  patchFileRecordCaches,
  removeFileRecordFromCaches,
  type FileRecordPatch,
} from "./fileRecordCache";
import {
  FILE_RECORD_LIST_OPTIONS,
  fileRecordKeys,
  fileRecordListKey,
  fileRecordListScope,
  type FileRecordListScope,
  type RecoverableScope,
} from "./fileRecordKeys";
import { isApiError } from "./utils";

export {
  FILE_RECORD_LIST_OPTIONS,
  FILE_RECORD_LIST_REFETCH_INTERVAL_MS,
  fileRecordKeys,
  fileRecordListKey,
  fileRecordListScope,
  type FileRecordListScope,
  type RecoverableScope,
} from "./fileRecordKeys";

function fileObjectKind(
  record: Pick<FileRecord, "kind" | "path">,
): "file" | "folder" {
  return record.kind === "folder" || record.path.endsWith("/")
    ? "folder"
    : "file";
}

/** API returns this while the file record is still processing. */
const TIPTAP_COLLAB_PROCESSING_PENDING_MESSAGE =
  "Collaboration is only available for files that have finished processing.";

const COLLABORATION_SETUP_MAX_RETRIES = 90;

export function isTiptapCollaborationProcessingPendingError(
  err: unknown,
): boolean {
  if (
    err instanceof Error &&
    err.message === TIPTAP_COLLAB_PROCESSING_PENDING_MESSAGE
  )
    return true;
  if (!isAxiosError(err)) return false;
  const data = err.response?.data as
    | { error?: { message?: string } }
    | undefined;
  return data?.error?.message === TIPTAP_COLLAB_PROCESSING_PENDING_MESSAGE;
}

/**
 * The record is gone for this viewer: archived (404) or never theirs to read
 * (403). Distinct from a transient failure, which must not blank a card we
 * already have content for.
 */
export function isFileRecordGoneError(err: unknown): boolean {
  const status = isApiError(err)
    ? err.status
    : isAxiosError(err)
      ? err.response?.status
      : undefined;
  return status === 404 || status === 403;
}

/** Matches backend UserMessage when TipTap HEAD finds no document yet. */
const TIPTAP_ENABLE_DOCUMENT_NOT_CREATED_MESSAGE =
  "Collaboration document has not been created yet.";

export function isTiptapEnableDocumentNotCreatedError(err: unknown): boolean {
  if (
    err instanceof Error &&
    err.message === TIPTAP_ENABLE_DOCUMENT_NOT_CREATED_MESSAGE
  )
    return true;
  if (!isAxiosError(err)) return false;
  // Backend migrated this from 400 to 422 (transient "not ready yet" state).
  // Accept both during rollout so retries keep working regardless of
  // which side ships first.
  const status = err.response?.status;
  if (status !== 400 && status !== 422) return false;
  const data = err.response?.data as
    | { error?: { message?: string } }
    | undefined;
  return data?.error?.message === TIPTAP_ENABLE_DOCUMENT_NOT_CREATED_MESSAGE;
}

/** Thrown from {@link useFileRecordCollaborationToken} when the tiptap-token response has no `document_name`. */
export class FileRecordTiptapDocumentNamePendingError extends Error {
  readonly name = "FileRecordTiptapDocumentNamePendingError";
  constructor() {
    super("Tiptap document name not yet available for this file record.");
  }
}

/** Thrown from {@link useFileRecordCollaborationToken} when `getById` has a status other than `processed`. */
export class FileRecordStatusNotProcessedError extends Error {
  readonly name = "FileRecordStatusNotProcessedError";
  constructor() {
    super("File record is not processed yet.");
  }
}

/**
 * Thrown from {@link useFileRecordCollaborationToken} when the row carries
 * bytes but no link: reading that as "no content to seed" hands the editor an
 * empty document, which `tiptapEnable` then makes the file's content.
 */
export class FileRecordCollaborationSeedUnresolvedError extends Error {
  readonly name = "FileRecordCollaborationSeedUnresolvedError";
  constructor() {
    super("File record has content but no link to seed it from.");
  }
}

export function isFileRecordCollaborationSeedUnresolvedError(
  err: unknown,
): err is FileRecordCollaborationSeedUnresolvedError {
  return err instanceof FileRecordCollaborationSeedUnresolvedError;
}

export function isFileRecordTiptapDocumentNamePendingError(
  err: unknown,
): err is FileRecordTiptapDocumentNamePendingError {
  return err instanceof FileRecordTiptapDocumentNamePendingError;
}

export function isFileRecordStatusNotProcessedError(
  err: unknown,
): err is FileRecordStatusNotProcessedError {
  return err instanceof FileRecordStatusNotProcessedError;
}

export type FileRecordCollaborationTokenResult = {
  fileRecord: FileRecord;
  token: string;
  can_write: boolean;
  expires_at: string;
  document_name: string;
  tiptap_enabled: boolean;
};

function collaborationSetupRetry(
  failureCount: number,
  error: unknown,
): boolean {
  // A link that did not come back once may come back now; a second miss is a
  // stable answer (a reference the viewer cannot read, a presign that keeps
  // failing) and the editor must say so rather than wait.
  if (isFileRecordCollaborationSeedUnresolvedError(error)) {
    return failureCount < 1;
  }
  if (failureCount >= COLLABORATION_SETUP_MAX_RETRIES) return false;
  return (
    isFileRecordTiptapDocumentNamePendingError(error) ||
    isFileRecordStatusNotProcessedError(error) ||
    isTiptapCollaborationProcessingPendingError(error)
  );
}

/**
 * Loads the file record, then mints a TipTap collab JWT. Retries when the
 * token response lacks `document_name` or when the token endpoint reports processing not finished.
 */
export function useFileRecordCollaborationToken(
  fileRecordId: string | undefined,
  options?: Pick<
    UseQueryOptions<FileRecordCollaborationTokenResult>,
    "enabled"
  >,
) {
  return useQuery({
    queryKey: fileRecordKeys.collaborationToken(fileRecordId ?? ""),
    queryFn: async (): Promise<FileRecordCollaborationTokenResult> => {
      if (!fileRecordId) throw new Error("File record id required");
      // Never a cached row: the seed decision reads `url`, and a restored row has none.
      const fileRecord = await orgAPI.fileRecords.getById(fileRecordId);

      if (fileRecord.status !== "processed") {
        throw new FileRecordStatusNotProcessedError();
      }

      const tokenPayload = await orgAPI.fileRecords.tiptapToken(fileRecordId);
      if (
        !tokenPayload.tiptap_enabled &&
        !fileRecord.url?.trim() &&
        (fileRecord.size ?? 0) > 0
      ) {
        throw new FileRecordCollaborationSeedUnresolvedError();
      }
      if (!tokenPayload.document_name?.trim()) {
        throw new FileRecordTiptapDocumentNamePendingError();
      }
      const { token, can_write, expires_at, document_name, tiptap_enabled } =
        tokenPayload;
      return {
        fileRecord,
        token,
        can_write,
        expires_at,
        document_name,
        tiptap_enabled,
      };
    },
    enabled: !!fileRecordId && (options?.enabled ?? true),
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    // The pending sentinels above are polling, not defects.
    meta: { sentry: false },
    retry: collaborationSetupRetry,
    retryDelay: (attemptIndex) =>
      Math.min(1000 * 2 ** Math.min(attemptIndex, 5), 30_000),
  });
}

/** Shared options for minting / refreshing the TipTap JWT (see {@link useFileRecordTiptapToken}). */
export function fileRecordTiptapTokenQueryOptions(fileRecordId: string) {
  return {
    queryKey: fileRecordKeys.tiptapToken(fileRecordId),
    queryFn: () => orgAPI.fileRecords.tiptapToken(fileRecordId),
  };
}

/**
 * TipTap Document Server JWT for a file record. Retries while the API reports
 * that collaboration is not ready until processing finishes.
 */
export function useFileRecordTiptapToken(
  fileRecordId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    ...fileRecordTiptapTokenQueryOptions(fileRecordId ?? ""),
    enabled: !!fileRecordId && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) =>
      isTiptapCollaborationProcessingPendingError(error) &&
      failureCount < COLLABORATION_SETUP_MAX_RETRIES,
    retryDelay: () => 2000,
  });
}

const WORKSPACE_TREE_KINDS: FileRecordKind[] = [
  "file",
  "folder",
  "link",
  "canvas",
];

/**
 * The workspace tree query — folders, canvases and files — and the ONLY
 * queryFn allowed to fill `fileRecordKeys.workspace(ws)`. Every hook,
 * `fetchQuery` and cold-cache seed for a workspace goes through this, so the
 * cache can never hold a files-only list that hides the canvases. (Optimistic
 * writers patch this key with `setQueryData`, which is exact-key: a second
 * cache for the same scope would miss all of them.)
 */
export function workspaceTreeQuery(workspaceId: string) {
  return {
    queryKey: fileRecordKeys.workspace(workspaceId),
    queryFn: () =>
      orgAPI.fileRecords.list({
        class: "workspace",
        workspace_id: workspaceId,
        kinds: WORKSPACE_TREE_KINDS,
      }),
  } as const;
}

/** The files-only view of the tree: everything but the canvas rows. */
export function selectWorkspaceFileRecords(rows: FileRecord[]): FileRecord[] {
  return rows.filter((row) => row.kind !== "canvas");
}

type WorkspaceTreeQueryOptions = Partial<
  Omit<
    UseQueryOptions<FileRecord[], AxiosError>,
    "queryKey" | "queryFn" | "select"
  >
>;

/** The workspace tree: folders, the canvases filed in them, and files. */
export function useWorkspaceTree(
  workspaceId: string | undefined,
  options?: WorkspaceTreeQueryOptions,
) {
  return useQuery({
    ...workspaceTreeQuery(workspaceId ?? ""),
    ...FILE_RECORD_LIST_OPTIONS,
    ...options,
    enabled: !!workspaceId && (options?.enabled ?? true),
  });
}

/** The same query narrowed to files, folders and references — for pickers. */
export function useWorkspaceFileRecords(
  workspaceId: string | undefined,
  options?: WorkspaceTreeQueryOptions,
) {
  return useQuery({
    ...workspaceTreeQuery(workspaceId ?? ""),
    ...FILE_RECORD_LIST_OPTIONS,
    ...options,
    select: selectWorkspaceFileRecords,
    enabled: !!workspaceId && (options?.enabled ?? true),
  });
}

/**
 * Files the user created (folders excluded), most recently modified first,
 * scoped server-side to workspaces and canvases they can still view plus their
 * own chats. Backs the home browser's Files · Recents view. `limit` caps the
 * page (server default 20, max 100); the endpoint supports `before_ts` keyset
 * pagination for future load-more.
 */
export function useRecentFileRecords(
  limit = 50,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  const query = useQuery({
    queryKey: fileRecordKeys.recent(limit),
    queryFn: async () => {
      const response = await orgAPI.fileRecords.listRecent({ limit });
      return response.file_records;
    },
    ...FILE_RECORD_LIST_OPTIONS,
    ...options,
  });

  return query;
}

/**
 * Fetch file records shared directly with the current user (SpiceDB
 * viewer/editor grants on the file itself), independent of parent-scope access.
 * Powers the "Shared with me" section in the Files rail.
 */
export function useSharedWithMeFileRecords(
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  const query = useQuery({
    queryKey: fileRecordKeys.sharedWithMe(),
    queryFn: () => orgAPI.fileRecords.listSharedWithMe(),
    ...FILE_RECORD_LIST_OPTIONS,
    ...options,
  });

  return query;
}

/**
 * Every `class = "canvas"` file record pinned to a canvas: the whole list,
 * for the explorer and the context menu. A node reads its own row through
 * {@link useFileRecordRow} instead.
 *
 * Public share SPA has no API auth — skip the list and rely on CDN-enriched
 * entity payloads (same gate as `useHydrateFileRecordEntity`).
 */
export function useCanvasFileRecords(
  canvasId: string | undefined,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  const isPublicCanvasView = useIsPublicCanvasView();
  return useQuery({
    ...fileRecordListQuery({ class: "canvas", canvas_id: canvasId ?? "" }),
    ...FILE_RECORD_LIST_OPTIONS,
    ...options,
    enabled: !!canvasId && !isPublicCanvasView && (options?.enabled ?? true),
  });
}

/** Every `class = "flow-session"` file record of a chat, for the Files-rail context section. */
export function useFlowSessionFileRecords(
  flowSessionId: string | undefined,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  return useQuery({
    ...fileRecordListQuery({
      class: "flow-session",
      flow_session_id: flowSessionId ?? "",
    }),
    ...FILE_RECORD_LIST_OPTIONS,
    ...options,
    enabled: !!flowSessionId && (options?.enabled ?? true),
  });
}

/**
 * List recently-deleted (recoverable) file records scoped to a workspace.
 *
 * The query intentionally does NOT populate `fileRecordKeys.detail(id)` —
 * the row carries `status: "deleted"` and partial fields, and would poison
 * the per-id cache that other surfaces (canvas tiles, detail pane) read.
 * Recovery surfaces always render straight from this list.
 */
export function useRecentlyDeletedWorkspaceFileRecords(
  workspaceId: string | undefined,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  return useQuery({
    queryKey: fileRecordKeys.recentlyDeleted("workspace", workspaceId ?? ""),
    queryFn: () => {
      if (!workspaceId) throw new Error("Workspace ID required");
      return orgAPI.fileRecords.list({
        class: "workspace",
        workspace_id: workspaceId,
        recently_deleted: true,
      });
    },
    ...FILE_RECORD_LIST_OPTIONS,
    ...options,
    enabled: !!workspaceId && (options?.enabled ?? true),
  });
}

/**
 * List recently-deleted (recoverable) file records pinned to a canvas. See
 * {@link useRecentlyDeletedWorkspaceFileRecords} for cache shape notes — the
 * canvas-scoped query mirrors it.
 */
export function useRecentlyDeletedCanvasFileRecords(
  canvasId: string | undefined,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  return useQuery({
    queryKey: fileRecordKeys.recentlyDeleted("canvas", canvasId ?? ""),
    queryFn: () => {
      if (!canvasId) throw new Error("Canvas ID required");
      return orgAPI.fileRecords.list({
        class: "canvas",
        canvas_id: canvasId,
        recently_deleted: true,
      });
    },
    ...FILE_RECORD_LIST_OPTIONS,
    ...options,
    enabled: !!canvasId && (options?.enabled ?? true),
  });
}

export interface FileRecordListQuery {
  readonly queryKey: readonly unknown[];
  readonly queryFn: () => Promise<FileRecord[]>;
}

/** The query that fills a scope's list: the only writer of that key's server rows. */
export function fileRecordListQuery(
  scope: FileRecordListScope,
): FileRecordListQuery {
  switch (scope.class) {
    case "workspace":
      return workspaceTreeQuery(scope.workspace_id);
    case "canvas":
      return {
        queryKey: fileRecordKeys.canvas(scope.canvas_id),
        queryFn: () =>
          orgAPI.fileRecords.list({
            class: "canvas",
            canvas_id: scope.canvas_id,
          }),
      } as const;
    case "flow-session":
      return {
        queryKey: fileRecordKeys.flowSession(scope.flow_session_id),
        queryFn: () =>
          orgAPI.fileRecords.list({
            class: "flow-session",
            flow_session_id: scope.flow_session_id,
          }),
      } as const;
  }
}

/**
 * `FileRecord` (api-client) uses `path` instead of `title`, but
 * `BaseFileRecord` (api-entities) requires `title` on the entity data
 * payload. Derive it from the path leaf once, here, so every builder below
 * is consistent.
 */
function fileRecordToEntityData(record: FileRecord) {
  const segments = record.path?.split("/").filter(Boolean) ?? [];
  const title = segments[segments.length - 1] || record.id;
  return { ...record, title };
}

function isCompleteFileRecordWire(d: unknown): d is FileRecord {
  if (!d || typeof d !== "object") return false;
  const o = d as Partial<FileRecord>;
  return (
    typeof o.id === "string" &&
    typeof o.path === "string" &&
    o.path.length > 0 &&
    typeof o.content_type === "string" &&
    typeof o.organization_id === "string"
  );
}

/**
 * Treat a presign as dead this long before its stated expiry, so a URL handed
 * out now cannot die between render and the fetch it feeds.
 */
const PRESIGN_EXPIRY_LEAD_MS = 30_000;

/** Earliest expiry across a row's presigned fields; null when none carries one. */
export function earliestPresignExpiryMs(
  record: FileRecord | null,
): number | null {
  if (!record) return null;
  let earliest: number | null = null;
  for (const url of [
    record.url,
    record.download_url,
    ...Object.values(record.preview_urls ?? {}),
  ]) {
    const expiresAt = getPresignedUrlExpiresAtMs(url);
    if (expiresAt !== null && (earliest === null || expiresAt < earliest)) {
      earliest = expiresAt;
    }
  }
  return earliest;
}

/**
 * Spread over which a tab draws its renewal lead, once at load: a pinned link
 * expires on a window boundary, so one fixed lead would have every open tab
 * re-read its lists in the same minute.
 */
const RENEWAL_SPREAD_MS = 10 * 60_000;

/** This tab's lead: {@link PRESIGN_EXPIRY_LEAD_MS} plus its draw. */
export const RENEWAL_LEAD_MS =
  PRESIGN_EXPIRY_LEAD_MS + Math.random() * RENEWAL_SPREAD_MS;

/**
 * Whether a row with usable links should have its source re-read for fresh
 * ones: inside this tab's lead, and not within a lead of the answer it would
 * renew — which bounds the cadence for a link signed to live less than the
 * lead. A link already expired when its answer arrived reports a skewed
 * clock, as it does to `ready`, and re-signing cannot fix that.
 */
export function dueForRenewal(
  expiresAt: number | null,
  answeredAt: number,
  now: number,
): boolean {
  if (expiresAt === null || expiresAt <= answeredAt) return false;
  return (
    expiresAt - RENEWAL_LEAD_MS <= now && answeredAt + RENEWAL_LEAD_MS <= now
  );
}

/** A re-read already running answers this consumer too. */
const JOIN_RUNNING_REFETCH = { cancelRefetch: false } as const;

export interface FileRecordRow {
  /**
   * The row with usable links: the source's current row once the server
   * answered this session and its links have life left, else the last such
   * row this hook handed out for the id, kept while the source is re-read. A
   * consumer that mounts on a row with dead links gets none and shows its
   * placeholder rather than the link.
   */
  record: FileRecord | undefined;
  /** No row to show yet: the source is in flight, or being re-read for live links. */
  isLoading: boolean;
  isError: boolean;
  /** 404 or 403: the file is gone for this viewer. */
  isGone: boolean;
  /** A byte request on one of the row's links failed: re-read the row's source, once per row. The consumer retries when the next row arrives. */
  refresh: () => void;
}

const NO_ROW: FileRecordRow = {
  record: undefined,
  isLoading: false,
  isError: false,
  isGone: false,
  refresh: () => {},
};

/**
 * Rows from one scope, by their own id or through the reference row that
 * points at each; the consumer re-renders only when one of its rows changes.
 * With no scope, or when the loaded list has no row for an id, that row comes
 * from `GET file-records/:id`. A row not ready to paint has its source
 * re-read, once per row, as does one whose links are near their expiry.
 */
export function useFileRecordRows(
  ids: readonly string[],
  scope: FileRecordListScope | undefined,
): { rows: ReadonlyMap<string, FileRecordRow>; settled: boolean } {
  const queryClient = useQueryClient();
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
  const idsKey = uniqueIds.join("|");
  const listKey = scope ? fileRecordListKey(scope) : undefined;
  const listHash = listKey ? hashKey(listKey) : "";
  const selectRows = useCallback(
    (rows: FileRecord[]) => uniqueIds.map((id) => findRowInList(rows, id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey, listHash],
  );
  const [list] = useQueries({
    queries:
      scope && listKey && uniqueIds.length > 0
        ? ([
            {
              ...fileRecordListQuery(scope),
              ...FILE_RECORD_LIST_OPTIONS,
              select: selectRows,
            },
          ] as UseQueryOptions<
            FileRecord[],
            AxiosError,
            (FileRecord | null)[]
          >[])
        : [],
  });
  const listed = list?.data;

  const fallbackIds = uniqueIds.filter(
    (_, index) =>
      !scope ||
      (listed?.[index] === null &&
        !isRestoredQueryData(list?.dataUpdatedAt ?? 0)),
  );
  const singles = useQueries({
    queries: fallbackIds.map((id) => ({
      queryKey: fileRecordKeys.detail(id),
      queryFn: () => orgAPI.fileRecords.getById(id),
      ...FILE_RECORD_LIST_OPTIONS,
    })) as UseQueryOptions<FileRecord, AxiosError>[],
  });

  // Judged on each render against when the source last answered. A link
  // already past its expiry when the row arrived reports a skewed clock, not
  // an aged-out link: re-signing cannot fix that, and acting on it would
  // re-read forever. `dataUpdatedAt` is read off the result only when the
  // verdict depends on it: that read is what re-renders the consumer when an
  // equal re-read advances it, and a row with life left owes no render to
  // re-reads that change nothing of its own.
  const ready = (
    row: FileRecord,
    key: QueryKey,
    source: { dataUpdatedAt: number },
  ) => {
    const expiresAt = earliestPresignExpiryMs(row);
    const aging =
      expiresAt !== null && expiresAt - PRESIGN_EXPIRY_LEAD_MS <= Date.now();
    if (
      !aging &&
      !isRestoredQueryData(queryClient.getQueryState(key)?.dataUpdatedAt ?? 0)
    )
      return true;
    const at = source.dataUpdatedAt;
    return !isRestoredQueryData(at) && !(aging && expiresAt > at);
  };

  // Renewed before the links die rather than after: `ready` still calls them
  // usable, so nothing blanks while the re-read runs. The answer time comes
  // off the cache, not the result, which would subscribe every consumer to a
  // re-read that changes nothing of its own.
  const renewing = (row: FileRecord, key: QueryKey) =>
    dueForRenewal(
      earliestPresignExpiryMs(row),
      queryClient.getQueryState(key)?.dataUpdatedAt ?? 0,
      Date.now(),
    );

  const sourcesRef = useRef(new Map<string, QueryKey>());
  const rereadRef = useRef(new Map<string, number>());
  // One re-read per server answer, so an equal answer still allows the next.
  const reread = useCallback(
    (id: string) => {
      const key = sourcesRef.current.get(id);
      const at = key && queryClient.getQueryState(key)?.dataUpdatedAt;
      if (!key || at === undefined || rereadRef.current.get(id) === at) return;
      rereadRef.current.set(id, at);
      void queryClient.invalidateQueries(
        { queryKey: key, exact: true },
        JOIN_RUNNING_REFETCH,
      );
    },
    [queryClient],
  );
  const refreshersRef = useRef(new Map<string, () => void>());
  const heldRef = useRef(new Map<string, FileRecord>());

  const rows = new Map<string, FileRecordRow>();
  const held = new Map<string, FileRecord>();
  const stale: string[] = [];
  let fallbackIndex = 0;
  uniqueIds.forEach((id, index) => {
    const single =
      fallbackIds[fallbackIndex] === id ? singles[fallbackIndex++] : undefined;
    const source = single ?? list;
    if (!source) return;
    const key = single ? fileRecordKeys.detail(id) : listKey!;
    const row = (single ? single.data : listed?.[index]) ?? null;
    const current = row && ready(row, key, source) ? row : undefined;
    const isGone = !!single?.error && isFileRecordGoneError(single.error);
    const record = isGone ? undefined : (current ?? heldRef.current.get(id));
    if (record) held.set(id, record);
    sourcesRef.current.set(id, key);
    if (
      !current &&
      source.status === "success" &&
      (row || isRestoredQueryData(source.dataUpdatedAt))
    )
      stale.push(id);
    else if (current && renewing(current, key)) stale.push(id);
    const isError =
      !record &&
      !isGone &&
      (list?.status === "error" || single?.status === "error");
    let refresh = refreshersRef.current.get(id);
    if (!refresh) {
      refresh = () => reread(id);
      refreshersRef.current.set(id, refresh);
    }
    rows.set(id, {
      record,
      isLoading: !record && !isError && !isGone,
      isError,
      isGone,
      refresh,
    });
  });
  heldRef.current = held;

  useEffect(() => {
    stale.forEach(reread);
  });

  const settled = Array.from(rows.values()).every((r) => !r.isLoading);
  return { rows, settled };
}

/** One row, read the way {@link useFileRecordRows} reads many. */
export function useFileRecordRow(
  id: string | undefined,
  scope: FileRecordListScope | undefined,
  options?: { enabled?: boolean },
): FileRecordRow {
  const { rows } = useFileRecordRows(
    id && (options?.enabled ?? true) ? [id] : [],
    scope,
  );
  return (id && rows.get(id)) || NO_ROW;
}

/** Nothing to hydrate: no input, or an input carrying no file-record id. */
const unhydrated = {
  entity: null,
  record: undefined,
  isLoading: false,
  isError: false,
  isGone: false,
  isSettled: false,
  refresh: () => {},
} as const;

/**
 * The row behind a `base: "file_record"` entity (a wire payload of
 * `{id, title}`) or a plain id, as the entity a card renders. The row is read
 * the one way ({@link useFileRecordRow}).
 */
export function useHydrateFileRecordEntity(
  input:
    | string
    | { base: "file_record"; data: unknown; platform: string }
    | null
    | undefined,
  scope: FileRecordListScope | undefined,
): {
  entity: Entity | null;
  record: FileRecord | undefined;
  isLoading: boolean;
  isError: boolean;
  /**
   * The record is definitively gone for this viewer (see
   * {@link isFileRecordGoneError}) — the one failure a caller may render as a
   * dead reference. `isError` alone must never mean that: a 500, a timeout or
   * an offline tab all set it, and none of them says the file stopped
   * existing.
   */
  isGone: boolean;
  /**
   * The API answered for this record in this session, so the returned entity
   * is the server's word on it. False while the entity is still the Loro
   * snapshot, a fetch in flight or a row restored from disk — none of which
   * can be read as "this file has no content".
   */
  isSettled: boolean;
  refresh: () => void;
} {
  const isPublicCanvasView = useIsPublicCanvasView();
  const isIdMode = typeof input === "string";
  const entity = isIdMode ? null : (input ?? null);
  const raw = entity?.data;
  const id = isIdMode
    ? input
    : raw && typeof raw === "object" && "id" in raw
      ? String((raw as { id: unknown }).id)
      : "";
  const platform = (!isIdMode && entity?.platform) || "cohesive";

  const { record, isLoading, isError, isGone, refresh } = useFileRecordRow(
    id || undefined,
    scope,
    {
      // The public share SPA has no API auth: the published payload is the only truth.
      enabled: !isPublicCanvasView,
    },
  );

  return useMemo(() => {
    if (!id) return unhydrated;
    if (isPublicCanvasView) {
      return entity
        ? {
            ...unhydrated,
            entity: entity as unknown as Entity,
            isSettled: true,
          }
        : { ...unhydrated, isError: true };
    }
    // Gone is decided before anything is served: neither the Loro snapshot
    // nor the retained row is worth rendering — both would show a file
    // nobody can open.
    if (isGone)
      return { ...unhydrated, isError: true, isGone: true, isSettled: true };
    const build = (data: Record<string, unknown>) =>
      ({
        type: "file_record",
        base: "file_record",
        platform,
        data,
      }) as unknown as Entity;
    if (!record) {
      // A Loro copy of the whole row paints its metadata meanwhile. Its links
      // were signed when the node was created and are long dead: drop them.
      const wire = isCompleteFileRecordWire(raw)
        ? build(
            dropPresignedFields(fileRecordToEntityData(raw)).data as Record<
              string,
              unknown
            >,
          )
        : null;
      return {
        ...unhydrated,
        entity: wire,
        isLoading: isLoading && !wire,
        isError,
        refresh,
      };
    }
    const baseData = isIdMode ? {} : typeof raw === "object" && raw ? raw : {};
    return {
      entity: build({ ...baseData, ...fileRecordToEntityData(record) }),
      record,
      isLoading: false,
      isError: false,
      isGone: false,
      isSettled: true,
      refresh,
    };
  }, [
    id,
    isPublicCanvasView,
    entity,
    isGone,
    record,
    isLoading,
    isError,
    refresh,
    raw,
    isIdMode,
    platform,
  ]);
}

interface RecoverContext {
  /** Cache key for the deleted list we optimistically removed from. */
  deletedKey: readonly unknown[];
  /** Snapshot to put back if the mutation fails. */
  removedRecord?: FileRecord;
}

/**
 * Recover (un-delete) a file record. Optimistically removes the row from the
 * deleted-list cache and, on success, invalidates the matching active list
 * so the recovered record reappears at its original path.
 *
 * Caller supplies `{ scope, scopeId }` so the hook knows which deleted-list
 * cache to mutate and which active list to invalidate. We accept the full
 * record in the mutation variables (not just the id) so the optimistic
 * removal can find the row without a lookup.
 */
export function useRecoverFileRecord(
  scope: RecoverableScope,
  scopeId: string | undefined,
  options?: UseMutationOptions<FileRecord, AxiosError, { record: FileRecord }>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ record }: { record: FileRecord }) =>
      orgAPI.fileRecords.recover(record.id),
    ...options,
    onMutate: async ({ record }): Promise<RecoverContext> => {
      const deletedKey = fileRecordKeys.recentlyDeleted(scope, scopeId ?? "");
      await queryClient.cancelQueries({ queryKey: deletedKey });
      const current = queryClient.getQueryData<FileRecord[]>(deletedKey);
      const removedRecord = current?.find((r) => r.id === record.id);
      queryClient.setQueryData<FileRecord[]>(deletedKey, (prev) =>
        prev ? prev.filter((r) => r.id !== record.id) : prev,
      );
      return { deletedKey, removedRecord };
    },
    onError: (err, variables, context) => {
      const ctx = context as RecoverContext | undefined;
      if (ctx?.removedRecord && ctx.deletedKey) {
        const restored = ctx.removedRecord;
        queryClient.setQueryData<FileRecord[]>(ctx.deletedKey, (prev) => {
          if (!prev) return prev;
          if (prev.some((r) => r.id === restored.id)) return prev;
          return [...prev, restored];
        });
      }
      options?.onError?.(err, variables, context);
    },
    onSuccess: (recovered, variables, context) => {
      patchFileRecordCaches(queryClient, recovered);
      trackFileRestored({
        fileRecordId: recovered.id,
        objectKind: fileObjectKind(recovered),
        class: recovered.class,
        extension: extensionOf(recovered.path),
      });

      // Repopulate the active list at the right scope. We invalidate rather
      // than splice manually because the server already publishes a
      // file_record event the SSE handler also processes, and an explicit
      // refetch is the simplest way to converge with that path.
      if (recovered.class === "workspace" && recovered.workspace_id) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(recovered.workspace_id),
        });
      } else if (recovered.class === "canvas" && recovered.canvas_id) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.canvas(recovered.canvas_id),
        });
      }
      options?.onSuccess?.(recovered, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (scopeId) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.recentlyDeleted(scope, scopeId),
        });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

export function useCreateFileRecord(
  workspaceId: string | undefined,
  options?: UseMutationOptions<FileRecord, AxiosError, CreateFileRecordPayload>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateFileRecordPayload) =>
      orgAPI.fileRecords.create(payload),
    ...options,
    onSuccess: (...args) => {
      const [data] = args;
      if (workspaceId) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(workspaceId),
        });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      // Non-folder creates are covered by the upload events, not an outcome.
      if (fileObjectKind(data) === "folder") {
        trackFolderCreated({
          fileRecordId: data.id,
          objectKind: "folder",
          class: data.class,
        });
      }
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Note on optimistic updates and the event stream:
 *
 * Mutations here apply their change to every cached copy of the row before
 * the request fires so the UI feels instant. The `file_record.*` event that
 * follows re-reads the lists it touches, which converges on the same state.
 *
 * Rollback is intentionally surgical (put back / restore *only* the record
 * this mutation touched) so that concurrent fan-outs — e.g. cascade folder
 * delete via `Promise.all` — don't roll back records that other concurrent
 * mutations have already removed.
 *
 * `onSettled` invalidates the lists as a backstop for the rare case where the
 * websocket missed the event (reconnect window). The user already sees the
 * optimistic state, so the silent background refetch is essentially free.
 */

interface DeleteContext {
  /** Snapshot of the record we removed, so we can put it back on failure. */
  removedRecord?: FileRecord;
}

export function useDeleteFileRecord(
  workspaceId: string | undefined,
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const closePane = useClosePane();
  return useMutation({
    mutationFn: (fileRecordId: string) =>
      orgAPI.fileRecords.delete(fileRecordId),
    ...options,
    onMutate: async (fileRecordId): Promise<DeleteContext> => {
      const removedRecord = findCachedFileRecord(queryClient, fileRecordId);
      const home = fileRecordListScope(removedRecord);
      if (home) {
        await queryClient.cancelQueries({ queryKey: fileRecordListKey(home) });
      }
      removeFileRecordFromCaches(queryClient, fileRecordId);
      return { removedRecord };
    },
    onError: (err, fileRecordId, context) => {
      const ctx = context as DeleteContext | undefined;
      if (ctx?.removedRecord) {
        patchFileRecordCaches(queryClient, ctx.removedRecord);
      }
      options?.onError?.(err, fileRecordId, context);
    },
    onSuccess: (data, variables, context) => {
      const removed = (context as DeleteContext | undefined)?.removedRecord;
      trackFileArchived({
        fileRecordId: variables,
        ...(removed
          ? {
              objectKind: fileObjectKind(removed),
              class: removed.class,
              extension: extensionOf(removed.path),
            }
          : {}),
      });
      options?.onSuccess?.(data, variables, context);

      // If the open pane matches the file record detail route that is being deleted, close it.
      if (
        location.state?.rightPane?.pathname ===
        route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId: variables })
      ) {
        closePane("rightPane");
      }
    },
    onSettled: (data, error, variables, context) => {
      // Deleting ANY file record (a canvas file, a chat reference, a workspace
      // row) must refresh every file listing — the drawer, the rail, and the
      // grids all read these keys, and a stale row after a delete is a dead
      // 404 row. Sweeping the root key is cheap (small lists) and removes
      // every per-caller invalidation gap by construction.
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.all });
      queryClient.invalidateQueries({
        queryKey: fileRecordKeys.detail(variables),
      });
      if (workspaceId) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(workspaceId),
        });
        // The deleted record now belongs in the recently-deleted list.
        // Without this, the global 10s staleTime serves a stale cached
        // list and the just-deleted file is missing from the recovery
        // sheet if it was opened within that window.
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.recentlyDeleted("workspace", workspaceId),
        });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

interface MoveContext {
  /** Source cache key (may equal destKey for in-place moves). */
  sourceKey?: readonly unknown[];
  /** Destination cache key, only set for cross-scope moves. */
  destKey?: readonly unknown[];
  /**
   * The pre-mutation record snapshot. Putting just this row back on failure
   * (rather than a whole list) keeps concurrent mutations intact.
   */
  originalRecord?: FileRecord;
}

type MoveFileRecordSource = string | FileRecordListScope | undefined;

function queryKeysEqual(
  a: readonly unknown[] | undefined,
  b: readonly unknown[] | undefined,
): boolean {
  if (!a || !b) return false;
  return hashKey(a) === hashKey(b);
}

function listKeyFromComponents(
  components: FileRecordComponents,
): readonly unknown[] | undefined {
  const scope = fileRecordListScope(components);
  return scope ? fileRecordListKey(scope) : undefined;
}

function normalizeMoveSource(
  source: MoveFileRecordSource,
): FileRecordListScope | undefined {
  if (!source) return undefined;
  if (typeof source === "string") {
    return { class: "workspace", workspace_id: source };
  }
  return source;
}

function applyDestinationToRecord(
  record: FileRecord,
  destination: FileRecordComponents,
): FileRecord {
  return {
    ...record,
    class: destination.class,
    workspace_id: destination.workspace_id,
    canvas_id: destination.canvas_id,
    flow_session_id: destination.flow_session_id,
    path: destination.path,
    updated_at: new Date().toISOString(),
  };
}

export function useMoveFileRecord(
  source: MoveFileRecordSource,
  options?: UseMutationOptions<
    FileRecord,
    AxiosError,
    { fileRecordId: string; payload: MoveFileRecordPayload }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      fileRecordId,
      payload,
    }: {
      fileRecordId: string;
      payload: MoveFileRecordPayload;
    }) => orgAPI.fileRecords.move(fileRecordId, payload),
    ...options,
    onMutate: async ({ fileRecordId, payload }): Promise<MoveContext> => {
      const sourceScope = normalizeMoveSource(source);
      const sourceKey = sourceScope
        ? fileRecordListKey(sourceScope)
        : undefined;
      const destKey = listKeyFromComponents(payload.destination);
      const isSameList = queryKeysEqual(sourceKey, destKey);

      if (sourceKey) {
        await queryClient.cancelQueries({ queryKey: sourceKey });
      }
      if (destKey && !isSameList) {
        await queryClient.cancelQueries({ queryKey: destKey });
      }

      const originalRecord =
        (sourceKey
          ? queryClient
              .getQueryData<FileRecord[]>(sourceKey)
              ?.find((r) => r.id === fileRecordId)
          : undefined) ?? findCachedFileRecord(queryClient, fileRecordId);
      if (!originalRecord) return { sourceKey, destKey };

      // The patched row names its new scope, so it leaves the source list
      // and joins the destination list in the one write.
      patchFileRecordCaches(
        queryClient,
        applyDestinationToRecord(originalRecord, payload.destination),
      );
      return { sourceKey, destKey, originalRecord };
    },
    onSuccess: (data, variables, context) => {
      trackFileMoved({
        fileRecordId: data.id,
        objectKind: fileObjectKind(data),
        class: data.class,
        destinationClass: variables.payload.destination.class,
        extension: extensionOf(data.path),
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (err, variables, context) => {
      const ctx = context as MoveContext | undefined;
      if (ctx?.originalRecord) {
        patchFileRecordCaches(queryClient, ctx.originalRecord);
      }
      options?.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      const ctx = context as MoveContext | undefined;
      if (ctx?.sourceKey) {
        queryClient.invalidateQueries({ queryKey: ctx.sourceKey });
      }
      // Cross-scope move: refresh the destination scope too in case
      // the SSE event missed.
      if (ctx?.destKey && !queryKeysEqual(ctx.destKey, ctx.sourceKey)) {
        queryClient.invalidateQueries({ queryKey: ctx.destKey });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

interface RenameContext {
  /** Pre-mutation `path` and `updated_at`, put back on failure. */
  previous: FileRecordPatch;
  /** List cache key for the record's class scope (workspace / canvas / flow-session). */
  listKey?: readonly unknown[];
}

/**
 * Rename a file record in place. This is a thin wrapper over `move` with
 * `destination` set to the source's own scope (same class, same workspace /
 * canvas / flow_session id), changing only the leaf segment of `path`.
 *
 * Unlike `useMoveFileRecord` it routes list invalidation by `record.class`
 * (renames happen across all three classes) and patches the name into every
 * cached copy synchronously, so no header flashes the old name between Enter
 * and the event-driven re-read. The server response carries freshly signed
 * links for the new S3 key and replaces the optimistic row on success.
 *
 * Caller passes the *full* current record (not just an id) so we have
 * `class` + scope IDs without a second fetch.
 */
export function useRenameFileRecord(
  options?: UseMutationOptions<
    FileRecord,
    AxiosError,
    { record: FileRecord; newPath: string }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      record,
      newPath,
    }: {
      record: FileRecord;
      newPath: string;
    }) => {
      const payload: MoveFileRecordPayload = {
        destination: {
          class: record.class,
          workspace_id: record.workspace_id,
          canvas_id: record.canvas_id,
          flow_session_id: record.flow_session_id,
          path: newPath,
          is_folder: record.path.endsWith("/") || undefined,
        },
      };
      return orgAPI.fileRecords.move(record.id, payload);
    },
    ...options,
    onMutate: async ({ record, newPath }): Promise<RenameContext> => {
      const previous: FileRecordPatch = {
        id: record.id,
        path: record.path,
        updated_at: record.updated_at,
      };
      const optimistic: FileRecordPatch = {
        id: record.id,
        path: newPath,
        updated_at: new Date().toISOString(),
      };
      const scope = fileRecordListScope(record);
      const listKey = scope ? fileRecordListKey(scope) : undefined;
      // Written on both sides of the cancel: before, so no frame shows the
      // old name; again after, because a cancel reverts the query to the
      // state it held when its fetch started.
      patchFileRecordCaches(queryClient, optimistic);
      if (listKey) await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({
        queryKey: fileRecordKeys.detail(record.id),
        exact: true,
      });
      patchFileRecordCaches(queryClient, optimistic);
      return { previous, listKey };
    },
    onError: (err, variables, context) => {
      const ctx = context as RenameContext | undefined;
      if (ctx?.previous) patchFileRecordCaches(queryClient, ctx.previous);
      options?.onError?.(err, variables, context);
    },
    onSuccess: (data, variables, context) => {
      patchFileRecordCaches(queryClient, data);
      trackFileRenamed({
        fileRecordId: data.id,
        objectKind: fileObjectKind(data),
        class: data.class,
        extension: extensionOf(data.path),
      });
      options?.onSuccess?.(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      const ctx = context as RenameContext | undefined;
      if (ctx?.listKey) {
        queryClient.invalidateQueries({ queryKey: ctx.listKey });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

interface CopyContext {
  /**
   * Cache key for the destination scope's listing. Set for any
   * `destination.class` we know how to list.
   */
  destKey?: readonly unknown[];
}

export function useCopyFileRecord(
  workspaceId: string | undefined,
  options?: UseMutationOptions<
    FileRecord,
    AxiosError,
    { fileRecordId: string; payload: CopyFileRecordPayload }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      fileRecordId,
      payload,
    }: {
      fileRecordId: string;
      payload: CopyFileRecordPayload;
    }) => orgAPI.fileRecords.copy(fileRecordId, payload),
    ...options,
    onMutate: async ({ payload }): Promise<CopyContext> => {
      // Copy mints a brand-new record id we don't know yet, so there's no
      // safe optimistic insert. We just preempt the query to prevent stale
      // refetches from racing the event-driven re-read, then let the
      // `file_record.created` event populate the destination list.
      const destKey = listKeyFromComponents(payload.destination);
      if (!destKey) return {};
      await queryClient.cancelQueries({ queryKey: destKey });
      return { destKey };
    },
    onSuccess: (data, variables, context) => {
      trackFileCopied({
        fileRecordId: data.id,
        objectKind: fileObjectKind(data),
        class: data.class,
        destinationClass: variables.payload.destination.class,
        extension: extensionOf(data.path),
      });
      options?.onSuccess?.(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      const ctx = context as CopyContext | undefined;
      if (workspaceId) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(workspaceId),
        });
      }
      if (ctx?.destKey) {
        queryClient.invalidateQueries({ queryKey: ctx.destKey });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

/**
 * Add a canvas/chat file to its workspace tree (A3): the record keeps its
 * id and moves to workspace class; a reference is left behind in the source
 * container so the file stays visible where it was. Invalidates the source
 * container's list, the destination workspace list, and the per-id detail cache.
 */
export function useAddFileRecordToLibrary(
  options?: UseMutationOptions<
    AddFileRecordToWorkspaceResponse,
    AxiosError,
    {
      record: FileRecord;
      workspaceId?: string;
      parentPath?: string;
      /** A block's live node box, when the invoking surface has it — the
       *  promoted record remembers the size the user last saw it at. */
      displaySize?: { width: number; height: number };
    }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ record, workspaceId, parentPath, displaySize }) =>
      orgAPI.fileRecords.addToWorkspace(record.id, {
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
        ...(parentPath ? { parent_path: parentPath } : {}),
        ...(displaySize
          ? {
              display_width: displaySize.width,
              display_height: displaySize.height,
            }
          : {}),
      }),
    ...options,
    onSuccess: (data, variables, context) => {
      trackFileMoved({
        fileRecordId: variables.record.id,
        objectKind: fileObjectKind(variables.record),
        class: variables.record.class,
        destinationClass: "workspace",
        extension: extensionOf(variables.record.path),
      });
      options?.onSuccess?.(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      const { record } = variables;
      if (record.canvas_id) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.canvas(record.canvas_id),
        });
      }
      if (record.flow_session_id) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.flowSession(record.flow_session_id),
        });
      }
      const destWorkspaceId =
        data?.file_record.workspace_id ?? variables.workspaceId;
      if (destWorkspaceId) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(destWorkspaceId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: fileRecordKeys.detail(record.id),
      });
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

/**
 * Add a reference to a workspace file in a canvas or chat (A2). One reference
 * per file per container — a re-add returns the existing row.
 */
export function useCreateFileRecordReference(
  options?: UseMutationOptions<
    CreateFileRecordReferenceResponse,
    AxiosError,
    { fileRecordId: string; payload: CreateFileRecordReferencePayload }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileRecordId, payload }) =>
      orgAPI.fileRecords.createReference(fileRecordId, payload),
    ...options,
    onSuccess: (data, variables, context) => {
      // Same row the list would return, so the node the caller places next
      // reads it out of the container's list instead of racing its own creation.
      patchFileRecordCaches(queryClient, data.file_record);
      options?.onSuccess?.(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (variables.payload.canvas_id) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.canvas(variables.payload.canvas_id),
        });
      }
      if (variables.payload.flow_session_id) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.flowSession(
            variables.payload.flow_session_id,
          ),
        });
      }
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

/**
 * Publish a file record so its `public/{file_id}` URL becomes live. The
 * response patches every cached copy so any pane already rendering this
 * record (e.g. `HtmlContent`'s publish button) reflects the change without
 * a refetch.
 */
export function usePublishFileRecord(
  options?: UseMutationOptions<FileRecord, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileRecordId: string) =>
      orgAPI.fileRecords.publish(fileRecordId),
    ...options,
    onSuccess: (record, variables, context) => {
      patchFileRecordCaches(queryClient, record);
      trackFilePublished({
        fileRecordId: record.id,
        objectKind: fileObjectKind(record),
        class: record.class,
        extension: extensionOf(record.path),
      });
      options?.onSuccess?.(record, variables, context);
    },
  });
}

export function useUnpublishFileRecord(
  options?: UseMutationOptions<FileRecord, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileRecordId: string) =>
      orgAPI.fileRecords.unpublish(fileRecordId),
    ...options,
    onSuccess: (record, variables, context) => {
      patchFileRecordCaches(queryClient, record);
      trackFileUnpublished({
        fileRecordId: record.id,
        objectKind: fileObjectKind(record),
        class: record.class,
        extension: extensionOf(record.path),
      });
      options?.onSuccess?.(record, variables, context);
    },
  });
}

/**
 * Publish many file records in one mutation. Wraps `publishMany`'s
 * `Promise.allSettled` semantics so the caller receives per-id outcomes;
 * every fulfilled row patches its cached copies, as the single-record hook does.
 */
export function usePublishFileRecords(
  options?: UseMutationOptions<PublishManyResult[], AxiosError, string[]>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileRecordIds: string[]) =>
      orgAPI.fileRecords.publishMany(fileRecordIds),
    ...options,
    onSuccess: (results, variables, context) => {
      let published = 0;
      for (const r of results) {
        if (r.status === "fulfilled") {
          published++;
          patchFileRecordCaches(queryClient, r.file_record);
        }
      }
      if (published > 0) {
        trackFilePublished({ count: published });
      }
      options?.onSuccess?.(results, variables, context);
    },
  });
}

/** Mirrors `usePublishFileRecords`. */
export function useUnpublishFileRecords(
  options?: UseMutationOptions<UnpublishManyResult[], AxiosError, string[]>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileRecordIds: string[]) =>
      orgAPI.fileRecords.unpublishMany(fileRecordIds),
    ...options,
    onSuccess: (results, variables, context) => {
      let unpublished = 0;
      for (const r of results) {
        if (r.status === "fulfilled") {
          unpublished++;
          patchFileRecordCaches(queryClient, r.file_record);
        }
      }
      if (unpublished > 0) {
        trackFileUnpublished({ count: unpublished });
      }
      options?.onSuccess?.(results, variables, context);
    },
  });
}

export function usePresignFileRecordUpload(
  options?: UseMutationOptions<PresignUploadResponse, AxiosError, string>,
) {
  return useMutation({
    mutationFn: (fileRecordId: string) =>
      orgAPI.fileRecords.presignUpload(fileRecordId),
    ...options,
  });
}

/**
 * Historical S3 object versions for a file record (last 30 days, processed
 * only, excluding current head — see {@link orgAPI.fileRecords.listVersions}).
 */
export function useFileRecordVersionsQuery(
  fileRecordId: string | undefined,
  options?: Pick<UseQueryOptions<FileRecordVersion[]>, "enabled">,
) {
  return useQuery({
    queryKey: fileRecordKeys.versions(fileRecordId ?? ""),
    queryFn: () => orgAPI.fileRecords.listVersions(fileRecordId!),
    enabled: !!fileRecordId && (options?.enabled ?? true),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useRestoreFileRecordVersionMutation(
  options?: UseMutationOptions<
    FileRecord,
    AxiosError,
    { fileRecordId: string; fileRecordVersionId: string }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileRecordId, fileRecordVersionId }) =>
      orgAPI.fileRecords.restoreVersion(fileRecordId, fileRecordVersionId),
    ...options,
    onSuccess: (record, variables, context) => {
      patchFileRecordCaches(queryClient, record);
      queryClient.invalidateQueries({
        queryKey: fileRecordKeys.versions(variables.fileRecordId),
      });
      trackFileVersionRestored({
        fileRecordId: record.id,
        objectKind: fileObjectKind(record),
        class: record.class,
        extension: extensionOf(record.path),
      });
      options?.onSuccess?.(record, variables, context);
    },
  });
}

/**
 * Patch the innerHTML of a named content region (`data-edit-id` element) within an HTML file record.
 * On success every cached copy is re-read for a fresh link; the
 * `file_record.updated` event drives the actual content re-fetch inside `HtmlContent`.
 */
export function usePatchFileRecordRegion(
  fileRecordId: string,
  options?: UseMutationOptions<
    PatchFileRecordRegionResponse,
    AxiosError,
    PatchFileRecordRegionPayload
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PatchFileRecordRegionPayload) =>
      orgAPI.fileRecords.patchRegion(fileRecordId, payload),
    ...options,
    onSuccess: (...args) => {
      invalidateCachedFileRecord(queryClient, fileRecordId);
      options?.onSuccess?.(...args);
    },
  });
}
